import { computed, hasInjectionContext, onScopeDispose, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';
import { useSdk } from '@fondationcss/sdk';
import {
  isWallGrouping,
  isWallNameFormat,
  isWallSort,
  type WallGrouping,
  type WallNameFormat,
  type WallSort,
} from '@/views/teaching/presence-wall-model';

export const TEACHING_GRID_DENSITIES = [
  { value: 'compact', title: 'Compact' },
  { value: 'normal', title: 'Normal' },
  { value: 'comfortable', title: 'Confortable' },
] as const;

export type TeachingGridDensity = typeof TEACHING_GRID_DENSITIES[number]['value'];
export type TeachingGridTextSize = 'auto' | 'small' | 'normal' | 'large';
export type TeachingGridDetails = 'auto' | 'show' | 'hide';

/**
 * How the Présences sheet is drawn: the classic table, or the ServerDataGrid look.
 * Only routes flagged `teachingGridStyles` offer the choice, the other teaching matrices
 * have a single rendering.
 */
export const TEACHING_GRID_STYLES = [
  { value: 'classic', title: 'Classique' },
  { value: 'grid', title: 'Grille' },
  { value: 'wall', title: 'Mur' },
] as const;

export type TeachingGridStyle = typeof TEACHING_GRID_STYLES[number]['value'];

export interface TeachingGridAppearance {
  density: TeachingGridDensity;
  textSize: TeachingGridTextSize;
  details: TeachingGridDetails;
  style: TeachingGridStyle;
  /** How a student is named inside a wall cell — the wall's only exposed setting (#814). */
  wallNameFormat: WallNameFormat;
  /** Which term orders students along the wall's path, inside their territory (#814). */
  wallSort: WallSort;
  /** What the wall tiles by — programme, competence, state, or nothing (#814). */
  wallGrouping: WallGrouping;
}

/**
 * The stored document: one appearance per page, keyed by route name. Per page, not shared by
 * the four grids, since the 2026-09-09 call (« j'émettrai par page »): the view one wants
 * compact is not the one one wants comfortable. `LEGACY_PAGE` holds a preference saved while
 * the settings were still shared: every page starts from it until it is set on its own.
 */
interface StoredAppearances {
  pages: Record<string, TeachingGridAppearance>;
}

const LEGACY_PAGE = '*';
/** The page key outside any route — the unit tests of other modules, mostly. */
const NO_ROUTE_PAGE = 'default';

const defaults = (): TeachingGridAppearance => ({
  density: 'normal',
  textSize: 'auto',
  details: 'auto',
  style: 'classic',
  wallNameFormat: 'firstInitial',
  wallSort: 'firstName',
  wallGrouping: 'program',
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** One appearance out of an unknown object, field by field: an invalid field takes its default. */
function parseAppearance(value: unknown): TeachingGridAppearance {
  if (!isRecord(value)) return defaults();
  return {
    density: isTeachingGridDensity(value.density) ? value.density : 'normal',
    textSize: isTextSize(value.textSize) ? value.textSize : 'auto',
    details: isDetails(value.details) ? value.details : 'auto',
    // A preference saved before the style existed has no key: it keeps the classic sheet.
    style: isTeachingGridStyle(value.style) ? value.style : 'classic',
    wallNameFormat: isWallNameFormat(value.wallNameFormat) ? value.wallNameFormat : 'firstInitial',
    wallSort: isWallSort(value.wallSort) ? value.wallSort : 'firstName',
    wallGrouping: isWallGrouping(value.wallGrouping) ? value.wallGrouping : 'program',
  };
}

function restoreAppearances(stored: string | null): StoredAppearances {
  // The very first format: a bare density string, shared by every grid.
  if (isTeachingGridDensity(stored)) return { pages: { [LEGACY_PAGE]: { ...defaults(), density: stored } } };
  try {
    const parsed: unknown = JSON.parse(stored ?? 'null');
    if (!isRecord(parsed)) return { pages: {} };
    if (isRecord(parsed.pages)) {
      const pages: Record<string, TeachingGridAppearance> = {};
      for (const [page, value] of Object.entries(parsed.pages)) {
        if (isRecord(value)) pages[page] = parseAppearance(value);
      }
      return { pages };
    }
    // The second format, one document shared by the four grids: every page starts from it.
    return { pages: { [LEGACY_PAGE]: parseAppearance(parsed) } };
  } catch { return { pages: {} }; }
}

export function isTeachingGridDensity(value: unknown): value is TeachingGridDensity {
  return TEACHING_GRID_DENSITIES.some(option => option.value === value);
}

export function isTeachingGridStyle(value: unknown): value is TeachingGridStyle {
  return TEACHING_GRID_STYLES.some(option => option.value === value);
}

function isTextSize(value: unknown): value is TeachingGridTextSize {
  return typeof value === 'string' && ['auto', 'small', 'normal', 'large'].includes(value);
}

function isDetails(value: unknown): value is TeachingGridDetails {
  return typeof value === 'string' && ['auto', 'show', 'hide'].includes(value);
}

/**
 * The appearance of the Parcours teaching matrices, per page and per signed-in user. Readers
 * (`density`, `textSize`, …) and writers (`setDensity`, …) address the CURRENT page — the route
 * the store was created under follows navigation, so the same store serves every grid.
 */
export const useTeachingGridDensity = defineStore('teaching-grid-density', () => {
  const sdk = useSdk();
  // Pinia runs this setup in the app's injection context, so the router's current route is
  // reachable; a store created outside any app (unit tests of other modules) has one page.
  const route = hasInjectionContext() ? useRoute() : null;
  const pageKey = computed(() => (route?.name ? String(route.name) : NO_ROUTE_PAGE));

  const appearances = ref<StoredAppearances>({ pages: {} });
  const storageKey = computed(() => sdk.me.value?.user.id
    ? `parcours:teaching-grid-density:${sdk.me.value.user.id}` : null);

  function restore() {
    let stored: string | null = null;
    try { stored = storageKey.value ? localStorage.getItem(storageKey.value) : null; }
    catch { /* Storage can be unavailable; the in-session setting still works. */ }
    appearances.value = restoreAppearances(stored);
  }

  watch(storageKey, restore, { immediate: true, flush: 'sync' });

  /** This page's appearance: its own, else the one saved while the settings were shared, else the defaults. */
  const current = computed(() =>
    appearances.value.pages[pageKey.value] ?? appearances.value.pages[LEGACY_PAGE] ?? defaults());

  function persist(): boolean {
    if (!storageKey.value) return false;
    try {
      localStorage.setItem(storageKey.value, JSON.stringify(appearances.value));
      return true;
    } catch { return false; }
  }

  function onStorage(event: StorageEvent) {
    if (event.key === storageKey.value || event.key === null) restore();
  }

  function update(patch: Partial<TeachingGridAppearance>): boolean {
    appearances.value.pages[pageKey.value] = { ...current.value, ...patch };
    return persist();
  }

  function setDensity(value: unknown): boolean {
    return isTeachingGridDensity(value) && update({ density: value });
  }

  function setTextSize(value: unknown): boolean {
    return isTextSize(value) && update({ textSize: value });
  }

  function setDetails(value: unknown): boolean {
    return isDetails(value) && update({ details: value });
  }

  function setStyle(value: unknown): boolean {
    return isTeachingGridStyle(value) && update({ style: value });
  }

  function setWallNameFormat(value: unknown): boolean {
    return isWallNameFormat(value) && update({ wallNameFormat: value });
  }

  function setWallSort(value: unknown): boolean {
    return isWallSort(value) && update({ wallSort: value });
  }

  function setWallGrouping(value: unknown): boolean {
    return isWallGrouping(value) && update({ wallGrouping: value });
  }

  /** Back to the defaults — for this page only. */
  function reset(): boolean {
    appearances.value.pages[pageKey.value] = defaults();
    return persist();
  }
  window.addEventListener('storage', onStorage);
  onScopeDispose(() => window.removeEventListener('storage', onStorage));

  return {
    /** The page whose settings are read and written. */
    page: pageKey,
    density: computed(() => current.value.density),
    compact: computed(() => current.value.density === 'compact'),
    textSize: computed(() => current.value.textSize),
    details: computed(() => current.value.details),
    hideStudentDetails: computed(() => current.value.details === 'hide'
      || (current.value.details === 'auto' && current.value.density === 'compact')),
    style: computed(() => current.value.style),
    isGridStyle: computed(() => current.value.style === 'grid'),
    isWallStyle: computed(() => current.value.style === 'wall'),
    wallNameFormat: computed(() => current.value.wallNameFormat),
    wallSort: computed(() => current.value.wallSort),
    wallGrouping: computed(() => current.value.wallGrouping),
    setDensity, setTextSize, setDetails, setStyle,
    setWallNameFormat, setWallSort, setWallGrouping, reset,
  };
});
